import type { Any } from '@/shared/constant/any';
import { usePersistFn } from 'ahooks';
import clsx from 'clsx';
import React from 'react';
import { Pane } from '../pane/Pane';
import { Resizer, RESIZER_DEFAULT_CLASSNAME } from '../resizer/Resizer';

export type SplitPaneProps = {
  allowResize?: boolean;
  children?: React.ReactNode;
  className?: string;
  primary?: 'first' | 'second';
  minSize?: number;
  maxSize?: number;
  defaultSize?: number;
  size?: number;
  split?: 'vertical' | 'horizontal';
  onDragStarted?: () => void;
  onDragFinished?: (draggedSize: number | undefined) => void;
  onChange?: (size: number) => void;
  onResizerClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  onResizerDoubleClick?: (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void;
  style?: React.CSSProperties;
  resizerStyle?: React.CSSProperties;
  paneClassName?: string;
  pane1ClassName?: string;
  pane2ClassName?: string;
  paneStyle?: React.CSSProperties;
  pane1Style?: React.CSSProperties;
  pane2Style?: React.CSSProperties;
  resizerClassName?: string;
  resizerLineClassName?: string;
  step?: number;
};

export function SplitPane(props: SplitPaneProps) {
  const {
    allowResize = true,
    children,
    className,
    primary = 'first',
    minSize = 50,
    maxSize,
    defaultSize,
    size,
    split = 'vertical',
    onDragStarted,
    onDragFinished,
    onChange,
    onResizerClick,
    onResizerDoubleClick,
    resizerStyle,
    paneClassName = '',
    pane1ClassName = '',
    pane2ClassName = '',
    paneStyle,
    pane1Style: pane1StyleProps,
    pane2Style: pane2StyleProps,
    resizerClassName,
    resizerLineClassName,
    style: styleProps,
    step,
  } = props;

  const pane1Ref = React.useRef<HTMLDivElement | null>();
  const pane2Ref = React.useRef<HTMLDivElement | null>();
  const splitPaneRef = React.useRef<HTMLDivElement | null>();

  const unFocus = usePersistFn(() => {
    try {
      if ('selection' in document) {
        (document as typeof Any).selection.empty();
      } else {
        window.getSelection()?.removeAllRanges();
      }
    } catch (_error) {
      if (_error) {
        //
      }
    }
  });

  const getDefaultSize = usePersistFn(
    (
      defaultSizeParameter: number | undefined,
      minSizeParameter: number | undefined,
      maxSizeParameter: number | undefined,
      draggedSizeParameter: number | undefined | null,
    ) => {
      if (typeof draggedSizeParameter === 'number') {
        const min = typeof minSizeParameter === 'number' ? minSizeParameter : 0;
        const max =
          typeof maxSizeParameter === 'number' && maxSizeParameter >= 0
            ? maxSizeParameter
            : Infinity;
        return Math.max(min, Math.min(max, draggedSizeParameter));
      }
      if (defaultSizeParameter !== undefined) {
        return defaultSizeParameter;
      }
      return minSizeParameter;
    },
  );

  const removeNullChildren = usePersistFn((child: React.ReactNode) => {
    return React.Children.toArray(child).filter((c) => c);
  });

  const [active, setActive] = React.useState(false);
  const [, setResized] = React.useState(false);
  const [initialSize] = React.useState(() => {
    return size !== undefined ? size : getDefaultSize(defaultSize, minSize, maxSize, null);
  });
  const [pane1Size, setPane1Size] = React.useState(() => {
    return primary === 'first' ? initialSize : undefined;
  });
  const [pane2Size, setPane2Size] = React.useState(() => {
    return primary === 'second' ? initialSize : undefined;
  });
  const [draggedSize, setDraggedSize] = React.useState<number | undefined>(undefined);
  const draggedSizeRef = React.useRef(draggedSize);
  draggedSizeRef.current = draggedSize;
  const [position, setPosition] = React.useState(0);
  const instanceProps = React.useRef(size);

  const styleElementRef = React.useRef<HTMLStyleElement | null>(null);
  const currentCursorStyleRef = React.useRef<string | null>(null);
  const setGlobalCursorStyle = usePersistFn(() => {
    const style = (() => {
      if (split === 'vertical') {
        return 'col-resize';
      } else {
        return 'row-resize';
      }
    })();

    if (currentCursorStyleRef.current === style) {
      return;
    }
    currentCursorStyleRef.current = style;
    if (styleElementRef.current === null) {
      styleElementRef.current = document.createElement('style');
      document.head.appendChild(styleElementRef.current);
    }

    styleElementRef.current.innerHTML = `*{cursor: ${style}!important;}`;
  });
  const resetGlobalCursorStyle = usePersistFn(() => {
    if (styleElementRef.current !== null) {
      document.head.removeChild(styleElementRef.current);

      currentCursorStyleRef.current = null;
      styleElementRef.current = null;
    }
  });

  const onMouseUp = usePersistFn(() => {
    if (allowResize && active) {
      onDragFinished?.(draggedSize);
      resetGlobalCursorStyle();
      setActive(false);
    }
  });
  const onTouchMove = usePersistFn(
    (event: {
      readonly touches: Record<
        number,
        {
          clientX: number;
          clientY: number;
        }
      >;
    }) => {
      if (allowResize && active) {
        unFocus();
        const isPrimaryFirst = primary === 'first';
        const ref = isPrimaryFirst ? pane1Ref.current : pane2Ref.current;
        const ref2 = isPrimaryFirst ? pane2Ref.current : pane1Ref.current;
        if (ref && ref2) {
          const node = ref;
          const node2 = ref2;

          if (node.getBoundingClientRect) {
            const width = node.getBoundingClientRect().width;
            const height = node.getBoundingClientRect().height;
            const current =
              (split === 'vertical' ? event.touches[0]?.clientX : event.touches[0]?.clientY) ?? 0;
            const currentSize = split === 'vertical' ? width : height;
            let positionDelta = position - current;
            if (step) {
              if (Math.abs(positionDelta) < step) {
                return;
              }
              // Integer division
              // eslint-disable-next-line no-bitwise
              positionDelta = ~~(positionDelta / step) * step;
            }
            let sizeDelta = isPrimaryFirst ? positionDelta : -positionDelta;

            const pane1Order = parseInt(window.getComputedStyle(node).order);
            const pane2Order = parseInt(window.getComputedStyle(node2).order);
            if (pane1Order > pane2Order) {
              sizeDelta = -sizeDelta;
            }

            let newMaxSize = maxSize ?? 0;
            if (maxSize !== undefined && maxSize <= 0) {
              const splitPane = splitPaneRef.current;
              if (split === 'vertical') {
                newMaxSize = (splitPane?.getBoundingClientRect()?.width ?? 0) + maxSize;
              } else {
                newMaxSize = (splitPane?.getBoundingClientRect()?.height ?? 0) + maxSize;
              }
            }

            let newSize = currentSize - sizeDelta;
            const newPosition = position - positionDelta;

            if (newSize < minSize) {
              newSize = minSize;
            } else if (maxSize !== undefined && newSize > newMaxSize) {
              newSize = newMaxSize;
            } else {
              setPosition(newPosition);
              setResized(true);
            }

            onChange?.(newSize);

            setDraggedSize(newSize);
            if (isPrimaryFirst) {
              setPane1Size(newSize);
            } else {
              setPane2Size(newSize);
            }
          }
        }
      }
    },
  );
  const onMouseMove = usePersistFn((event: MouseEvent) => {
    onTouchMove({
      touches: [{ clientX: event.clientX, clientY: event.clientY }],
    });
  });
  const onTouchStart = usePersistFn(
    (event: {
      readonly touches: Record<
        number,
        {
          clientX: number;
          clientY: number;
        }
      >;
    }) => {
      if (allowResize) {
        unFocus();
        const newPosition =
          (split === 'vertical' ? event.touches[0]?.clientX : event.touches[0]?.clientY) ?? 0;

        if (typeof onDragStarted === 'function') {
          onDragStarted();
        }
        setGlobalCursorStyle();
        setActive(true);
        setPosition(newPosition);
      }
    },
  );
  const onMouseDown = usePersistFn((event) => {
    const eventWithTouches = Object.assign({}, event, {
      touches: [{ clientX: event.clientX, clientY: event.clientY }],
    });
    onTouchStart(eventWithTouches);
  });

  React.useEffect(() => {
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('touchmove', onTouchMove);

    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('touchmove', onTouchMove);
    };
  }, [onMouseMove, onMouseUp, onTouchMove]);

  React.useEffect(() => {
    if (instanceProps.current === size && size !== undefined) {
      return;
    }

    const newSize =
      size !== undefined
        ? size
        : getDefaultSize(defaultSize, minSize, maxSize, draggedSizeRef.current);

    if (size !== undefined) {
      setDraggedSize(newSize);
    }

    const isPanel1Primary = primary === 'first';
    if (isPanel1Primary) {
      setPane1Size(newSize);
      setPane2Size(undefined);
    } else {
      setPane1Size(undefined);
      setPane2Size(newSize);
    }
    instanceProps.current = size;
  }, [defaultSize, getDefaultSize, maxSize, minSize, primary, size]);

  const disabledClass = allowResize ? '' : 'disabled';
  const resizerClassNamesIncludingDefault = resizerClassName
    ? `${resizerClassName} ${RESIZER_DEFAULT_CLASSNAME}`
    : resizerClassName;

  const notNullChildren = removeNullChildren(children);

  const style = React.useMemo<React.CSSProperties>(() => {
    const result = {
      display: 'flex',
      ...styleProps,
    } satisfies React.CSSProperties;
    if (split === 'vertical') {
      return {
        ...result,
        flexDirection: 'row',
      };
    } else {
      return {
        ...result,
        flexDirection: 'column',
      };
    }
  }, [split, styleProps]);

  const pane1Style = React.useMemo(() => {
    return { ...paneStyle, ...pane1StyleProps };
  }, [pane1StyleProps, paneStyle]);
  const pane2Style = React.useMemo(() => {
    return { ...paneStyle, ...pane2StyleProps };
  }, [pane2StyleProps, paneStyle]);

  return (
    <div
      className={clsx('SplitPane', split, className, disabledClass)}
      ref={(node) => {
        splitPaneRef.current = node;
      }}
      style={style}
    >
      <Pane
        className={clsx('Pane1', paneClassName, pane1ClassName)}
        key="pane1"
        eleRef={(node) => {
          pane1Ref.current = node;
        }}
        size={pane1Size}
        split={split}
        style={pane1Style}
      >
        {notNullChildren[0]}
      </Pane>
      <Resizer
        className={disabledClass}
        onClick={onResizerClick}
        onDoubleClick={onResizerDoubleClick}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchEnd={onMouseUp}
        key="resizer"
        resizerClassName={resizerClassNamesIncludingDefault}
        resizerLineClassName={resizerLineClassName}
        split={split}
        style={resizerStyle || {}}
        active={active}
      />
      <Pane
        className={clsx('Pane2', paneClassName, pane2ClassName)}
        key="pane2"
        eleRef={(node) => {
          pane2Ref.current = node;
        }}
        size={pane2Size}
        split={split}
        style={pane2Style}
      >
        {notNullChildren[1]}
      </Pane>
    </div>
  );
}
